// @flow
import moment from 'moment';
import {object, boolean, number, string, array} from 'yup';
import {RECURRENCE_FREQ_MAP} from 'modules/calendar/constants';

export const MAX_GROUP_NAME_LENGTH = 128;
export const MAX_GROUP_DESC_LENGTH = 256;

export const MAX_NUM_GROUP_EVENTS = 3;

// $FlowIgnore
export const groupSchema = object({
  name: string()
    .max(MAX_GROUP_NAME_LENGTH, 'Must be at most 128 characters')
    .required('Group Name is required')
    .default(''),
  description: string()
    .max(MAX_GROUP_DESC_LENGTH, 'Must be at most 128 characters')
    .optional()
    .default(''),
  programs: array().min(1, 'Program is required').required().default([]),
  category: string().required('Group Type is required').default(''),
  isPublic: boolean().required().default(true),
});

const defaultStart = moment();

export const groupEventSchema = object({
  id: string().default(''), // calendar event id
  groupEventId: string().default(''),
  createdBy: string().required('Created By is required').default(''),
  owner: string().required('Owner is required').default(''),
  title: string().required('Event Name is required').default(''),
  isAllDay: boolean().required().default(false),
  startDate: string()
    .required('Start Date is required')
    .default(defaultStart.toISOString()),
  startTime: string()
    .required('Start Date is required')
    .default(defaultStart.format('HH:00')),
  endTime: string()
    .required('End Date is required')
    .default(defaultStart.add(1, 'hours').format('HH:00')),
  timezone: string().required('Time Zone is required'),
  reminderTime: string().required('Reminder Time is required').default('60'),
  data: object({
    eventType: string().oneOf(['In person', 'Virtual']).default('In person'),
    notes: string().default(''),
    phone: string().default(''),
    link: string().default(''),
    location: object({
      name: string().max(128, 'Must be at most 128 characters').default(''),
      address1: string().default(''),
      address2: string().default(''),
      city: string().default(''),
      state: string().default(''),
      zip: string().default(''),
    }),
  }).test(
    'address-or-url',
    'You must either provide a URL or full address.',
    (value) => {
      const {link, location} = value || {};
      const {address1, city, state, zip} = location;

      const hasAddress = address1 && city && state && zip;
      const hasUrl = link && link.length > 0;

      return hasAddress || hasUrl;
    },
  ),
  status: string(),
  isRecurring: boolean().default(false),
  recurrence: object({
    interval: number().default(1),
    freq: string().default(RECURRENCE_FREQ_MAP.daily),
    bymonth: number().nullable().default(null),
    bymonthday: number().nullable().default(null),
    byyearday: number().nullable().default(null),
    byweekno: number().nullable().default(null),
    byweekday: string().nullable().default(null),
    bysetpos: number().nullable().default(null),
  }).default({}),
  originalStart: string().default(''),
  invitations: array().default([]),
});

export const REMINDER_INTERVALS = [
  {
    label: '15',
    value: '15',
    interval: 'minutes',
  },
  {
    label: '30',
    value: '30',
    interval: 'minutes',
  },
  {
    label: '1',
    value: '60',
    interval: 'hour',
  },
  {
    label: '2',
    value: '120',
    interval: 'hours',
  },
  {
    label: '1',
    value: '1440',
    interval: 'day',
  },
];

export const eventReminderIntervals = [
  {
    label: '15 minutes before',
    value: '15',
  },
  {
    label: '30 minutes before',
    value: '30',
  },
  {
    label: '1 hour before',
    value: '60',
  },
  {
    label: '2 hours before',
    value: '120',
  },
  {
    label: '1 day before',
    value: '1440',
  },
];

export const eventFormHelperText1 =
  'Does this group meet regularly? Set up recurring meeting invites and reminders.';
export const eventFormHelperTextEdit =
  'This group meets regularly. Open group schedule to view details.';
export const eventFormHelperText2 =
  'Does this group meet at an additional location or at a different time? Add another meeting series with reminders.';
